// Bar viz — horizontal bars sorted desc by value.
//
// Column resolution:
//   - panel.barLabelCol / panel.barValueCol (column names) take priority
//   - otherwise: col 0 = label, find the first numeric column for value.
//
// Optional row filter: panel.rowLabelIn = array of strings restricts the
// result to rows whose label column is in the set (mirrors kpiKeys, but
// here it filters the chart input). Useful for picking a subset out of
// SHOW METRICS-style label/value rows.
//
// Cap: panel.barLimit (default 12). Truncation foot is shown when capped.

function isNumericCell(v) {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'string') return /^-?\d+(\.\d+)?$/.test(v);
  return false;
}

function asNumber(v) {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) return parseFloat(v);
  return 0;
}

function fmtValue(v) {
  const n = asNumber(v);
  if (!Number.isFinite(n)) return String(v);
  if (Math.abs(n) >= 1024 ** 3) return (n / 1024 ** 3).toFixed(1) + ' GB';
  if (Math.abs(n) >= 1024 ** 2) return (n / 1024 ** 2).toFixed(1) + ' MB';
  if (Math.abs(n) >= 1024)      return (n / 1024).toFixed(1) + ' KB';
  if (Math.abs(n) >= 1000)      return n.toLocaleString();
  return String(v);
}

function pickColumns(columns, rows, panel) {
  if (!columns?.length) return null;
  let labelIdx = 0;
  let valueIdx = -1;
  if (panel?.barLabelCol) {
    const i = columns.indexOf(panel.barLabelCol);
    if (i >= 0) labelIdx = i;
  }
  if (panel?.barValueCol) {
    const i = columns.indexOf(panel.barValueCol);
    if (i >= 0) valueIdx = i;
  }
  if (valueIdx < 0) {
    // Pick the first column that's numeric in *any* row. SHOW DISK STATUS
    // mixes string and numeric values (head_hash is text, wal_size_bytes
    // is a number) so checking only row 0 misses the numeric column.
    for (let i = 0; i < columns.length; i++) {
      if (i === labelIdx) continue;
      if (rows?.some(r => isNumericCell(r[i]))) { valueIdx = i; break; }
    }
  }
  if (valueIdx < 0) return null;
  return { labelIdx, valueIdx };
}

export function BarViz({ columns, rows, error, loading, panel }) {
  if (error) return <div className="report-viz-empty error">{error}</div>;
  if (loading && !rows) return <div className="report-viz-empty">Loading…</div>;
  if (!rows?.length) return <div className="report-viz-empty">No rows</div>;

  const cols = pickColumns(columns, rows, panel);
  if (!cols) {
    return <div className="report-viz-empty">Bar viz needs a numeric column. None detected.</div>;
  }

  const limit = Number.isFinite(panel?.barLimit) ? panel.barLimit : 12;
  const filterSet = Array.isArray(panel?.rowLabelIn) && panel.rowLabelIn.length
    ? new Set(panel.rowLabelIn) : null;
  const filtered = filterSet ? rows.filter(r => filterSet.has(r[cols.labelIdx])) : rows;
  if (filterSet && !filtered.length) {
    return <div className="report-viz-empty">No matching rows for {panel.rowLabelIn.join(', ')}</div>;
  }
  const all = filtered
    .map(r => ({ label: String(r[cols.labelIdx] ?? ''), value: asNumber(r[cols.valueIdx]) }))
    .sort((a, b) => b.value - a.value);
  const shown = all.slice(0, limit);
  const max = Math.max(1, ...shown.map(b => b.value));

  return (
    <div className="report-viz-bar">
      {shown.map((b, i) => (
        <div key={i} className="report-viz-bar-row">
          <div className="report-viz-bar-label" title={b.label}>{b.label}</div>
          <div className="report-viz-bar-track">
            <div
              className="report-viz-bar-fill"
              style={{ width: `${(b.value / max) * 100}%` }}
            />
          </div>
          <div className="report-viz-bar-value">{fmtValue(b.value)}</div>
        </div>
      ))}
      {all.length > limit && (
        <div className="report-viz-foot">Showing top {limit} of {all.length}</div>
      )}
    </div>
  );
}
